import { useEffect, useRef, useState } from "react";
import { Pause, Play, Store, MapPin, Laptop, Star } from "lucide-react";
import { useMediaQuery } from "@/lib/site";

const POSTER = "/media/faiz-promo-poster.jpg";
const SRC = "/media/faiz-promo-720.mp4";

const Chip = ({ icon: Icon, className, delay }) => (
  <span
    aria-hidden="true"
    style={{ animationDelay: delay }}
    className={`absolute hidden h-12 w-12 animate-float place-items-center rounded-full bg-ink-2 text-emerald-dot shadow-soft ring-1 ring-white/10 sm:grid ${className}`}
  >
    <Icon className="h-5 w-5" strokeWidth={2} />
  </span>
);

const usePlayer = (reduced) => {
  const ref = useRef(null);
  const bar = useRef(null);
  const wantPlay = useRef(!reduced);
  const [state, setState] = useState(reduced ? "paused" : "loading");

  const play = () => {
    const v = ref.current;
    if (!v) return;
    wantPlay.current = true;
    const p = v.play();
    if (p) p.catch(() => setState((s) => (s === "error" ? s : "paused")));
  };
  const pause = () => {
    wantPlay.current = false;
    ref.current?.pause();
  };

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (reduced) { wantPlay.current = false; v.pause(); setState((s) => (s === "error" ? s : "paused")); }
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) v.pause();
      else if (wantPlay.current) play();
    }, { threshold: 0.2 });
    io.observe(v);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  const events = {
    onPlaying: () => setState("playing"),
    onPause: () => setState((s) => (s === "error" ? s : "paused")),
    onTimeUpdate: (e) => {
      const v = e.currentTarget;
      if (bar.current && v.duration) bar.current.style.transform = `scaleX(${v.currentTime / v.duration})`;
    },
  };
  return { ref, bar, state, setState, play, pause, events };
};

export default function HeroVideo() {
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { ref, bar, state, setState, play, pause, events } = usePlayer(reduced);
  const failed = state === "error";
  const playing = state === "playing";

  return (
    <div className="relative px-2 pb-4 pr-4 sm:px-6 sm:pb-6 sm:pr-6" data-testid="hero-video-frame">
      <div aria-hidden="true" className="absolute bottom-0 right-0 top-8 left-10 rounded-[30px] bg-emerald" />
      <figure className="relative rounded-[26px] bg-ink-2 p-2 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 sm:p-2.5">
        <div className="flex items-center gap-3 px-3 pb-2.5 pt-1.5">
          <span className="flex gap-1.5" aria-hidden="true">
            <i className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <i className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <i className="h-2.5 w-2.5 rounded-full bg-emerald-dot" />
          </span>
          <span className="mx-auto flex min-w-0 items-center gap-2 rounded-full bg-white/5 px-4 py-1 font-mono text-[11px] text-white/60">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-dot" /> www.faizu.net
          </span>
          <span className="w-10" aria-hidden="true" />
        </div>
        <div className="relative aspect-video overflow-hidden rounded-[18px] bg-soft">
          <img
            src={POSTER}
            alt="FAIZ promo film still: a shop owner in a green apron outside her storefront"
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="hero-video-poster"
            fetchPriority="high"
          />
          {!failed && (
            <video
              ref={ref}
              data-testid="hero-video"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${state === "loading" ? "opacity-0" : "opacity-100"}`}
              poster={POSTER}
              muted
              loop
              playsInline
              autoPlay={!reduced}
              preload="metadata"
              aria-label="FAIZ promo film"
              onLoadedData={() => setState((s) => (s === "loading" && reduced ? "paused" : s))}
              onError={() => setState("error")}
              {...events}
            >
              <source src={SRC} type="video/mp4" onError={() => setState("error")} />
            </video>
          )}
          {!failed && (
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-3 sm:p-4">
              <button
                type="button"
                onClick={playing ? pause : play}
                data-testid="hero-video-play-pause-btn"
                aria-label={playing ? "Pause promo film" : "Play promo film"}
                aria-pressed={playing}
                className="group flex h-11 items-center gap-2 rounded-full bg-white/95 pl-1.5 pr-4 text-ink shadow-soft ring-1 ring-line transition-transform duration-200 hover:scale-[1.03]"
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald text-white">
                  {playing ? <Pause className="h-3.5 w-3.5" fill="currentColor" data-testid="icon-pause" /> : <Play className="ml-0.5 h-3.5 w-3.5" fill="currentColor" data-testid="icon-play" />}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em]">{playing ? "Pause" : "Play"} film</span>
              </button>
              <span className="relative hidden h-[3px] flex-1 overflow-hidden rounded-full bg-white/70 sm:block" aria-hidden="true">
                <span ref={bar} className="absolute inset-0 origin-left scale-x-0 rounded-full bg-emerald" />
              </span>
            </div>
          )}
        </div>
      </figure>
      <Chip icon={Store} className="-left-1 top-[28%]" delay="0s" />
      <Chip icon={MapPin} className="-top-3 right-[18%]" delay="1.2s" />
      <Chip icon={Laptop} className="-left-3 bottom-[18%]" delay="2.1s" />
      <Chip icon={Star} className="-right-1 bottom-[30%]" delay="0.6s" />
    </div>
  );
}
